import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Tooltip, useMediaQuery, Drawer, Collapse } from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Person as PatientsIcon,
  Folder as SubmissionsIcon,
  AccountTree as ManagementIcon,
  LocalHospital as ClinicsIcon,
  Hub as DistributorsIcon,
  Inventory as ProductsIcon,
  Description as ResourcesIcon,
  Assessment as ReportsIcon,
  BarChart as AnalyticsIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
} from '@mui/icons-material';
import api from '../../utils/api';
import { displayRole } from '../../utils/roleDisplayLabels';
import { SessionTimeoutProvider } from '../../contexts/SessionTimeoutContext';
import { useTheme } from '../../context/ThemeContext';
import { useUser } from '../../contexts/UserContext';
import LogoIcon from '../common/MedNecessityLogo';
import './Layout.css';

interface NavItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  path: string;
  dividerAfter?: boolean;
}

/* ─── Inline svg icons (chevrons) ─── */
const ChevLeft = () => (
  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M10 3.5L5.5 8 10 12.5" />
  </svg>
);
const ChevDown = () => (
  <svg viewBox="0 0 16 16" width="11" height="11" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M3.5 6L8 10.5 12.5 6" />
  </svg>
);
const ChevUp = () => (
  <svg viewBox="0 0 16 16" width="11" height="11" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M3.5 10L8 5.5 12.5 10" />
  </svg>
);
const BurgerSvg = () => (
  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M2 4h12M2 8h12M2 12h12" />
  </svg>
);

const OrgLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDarkMode, toggleTheme } = useTheme();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const isTablet = useMediaQuery('(min-width: 769px) and (max-width: 1200px)');
  const isDesktop = useMediaQuery('(min-width: 1201px)');

  const [collapsed, setCollapsed] = useState(!isDesktop);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [adminExpanded, setAdminExpanded] = useState(true);

  React.useEffect(() => {
    if (isDesktop) setCollapsed(false);
    else if (isTablet) setCollapsed(true);
  }, [isDesktop, isTablet]);

  const { user, setUser } = useUser();
  const role = (user?.Role || user?.role || '').toLowerCase();
  const isViewOnly = role === 'org rep';
  const userName = user?.Name || user?.Username || user?.email || 'User';
  const userInitial = userName.charAt(0).toUpperCase();

  // Base nav: items every Org-tier user (Org Admin AND Org Rep) needs
  // day-to-day. Clinics is promoted near the top because that's the surface
  // most operators land on first when working an account.
  const baseNavItems: NavItem[] = [
    { id: 'dashboard', label: 'Dashboard', icon: <DashboardIcon />, path: '/org/dashboard' },
    { id: 'clinics', label: 'Clinics', icon: <ClinicsIcon />, path: '/org/clinics-view' },
    { id: 'distributors', label: 'Distributors', icon: <DistributorsIcon />, path: '/org/distributors-view' },
    { id: 'patients', label: 'Patients', icon: <PatientsIcon />, path: '/org/patients' },
    { id: 'submissions', label: 'Submissions', icon: <SubmissionsIcon />, path: '/org/submissions' },
    { id: 'products', label: 'Products', icon: <ProductsIcon />, path: '/org/products' },
    { id: 'resources', label: 'Resources', icon: <ResourcesIcon />, path: '/org/resources' },
  ];

  // Admin-only nav: things Org Reps can't (or shouldn't) access — grouped
  // visually under an "Admin" header so the role boundary is obvious.
  const adminNavItems: NavItem[] = isViewOnly
    ? []
    : [
        { id: 'management', label: 'Management', icon: <ManagementIcon />, path: '/org/management' },
        { id: 'reports', label: 'Reports', icon: <ReportsIcon />, path: '/org/reports' },
      ];

  const handleLogout = async () => {
    try {
      await api.post('/api/logout', {}, { withCredentials: true });
    } catch (error) {
      console.error('Error during logout:', error);
    } finally {
      setUser(null);
      navigate('/login', { replace: true });
    }
  };

  const isActivePath = (path: string) =>
    location.pathname === path || location.pathname.startsWith(path + '/');

  const handleNavigation = (path: string) => {
    navigate(path);
    if (isMobile) setMobileOpen(false);
  };

  const navWidth = collapsed ? 64 : 240;

  /* ─── Render a single nav item (with optional collapsed-state tooltip) ─── */
  const renderNavItem = (item: NavItem, key?: string | number) => {
    const active = isActivePath(item.path);
    const button = (
      <button
        type="button"
        className={`mn-nav-item${active ? ' is-active' : ''}`}
        onClick={() => handleNavigation(item.path)}
      >
        <span className="mn-nav-item-icon">{item.icon}</span>
        <span className="mn-nav-item-label">{item.label}</span>
      </button>
    );
    if (collapsed && !isMobile) {
      return (
        <Tooltip key={key ?? item.id} title={item.label} placement="right">
          {button}
        </Tooltip>
      );
    }
    return <React.Fragment key={key ?? item.id}>{button}</React.Fragment>;
  };

  const isCollapsed = collapsed && !isMobile;
  const handleBrandClick = () => {
    if (isCollapsed) setCollapsed(false);
    else navigate('/org/dashboard');
  };

  // Suppress unused-warning for view-only without removing the role check
  void isViewOnly;

  const NavContent = () => {
    const brandMark = (
      <div
        className="mn-nav-brand-mark"
        onClick={handleBrandClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleBrandClick(); }
        }}
      >
        <LogoIcon size={26} color="currentColor" className="mn-nav-brand-logo" />
        <span className="mn-nav-brand-text">MedNecessity</span>
      </div>
    );

    return (
      <nav className={`mn-nav${isCollapsed ? ' is-collapsed' : ''}${isDarkMode ? ' dark' : ''}`}>
        {/* Brand */}
        <div className="mn-nav-brand">
          {isCollapsed ? (
            <Tooltip title="Expand sidebar" placement="right">{brandMark}</Tooltip>
          ) : brandMark}
          {!isMobile && !collapsed && (
            <button
              type="button"
              className="mn-nav-collapse"
              onClick={() => setCollapsed(true)}
              aria-label="Collapse sidebar"
            >
              <ChevLeft />
            </button>
          )}
        </div>

        {/* Main list */}
        <div className="mn-nav-list">
          {baseNavItems.map((item) => (
            <React.Fragment key={item.id}>
              {renderNavItem(item)}
              {item.dividerAfter && (
                <div className="mn-nav-section" aria-hidden>
                  <span className="mn-nav-section-rule" />
                </div>
              )}
            </React.Fragment>
          ))}

          {/* Admin section — only Org Admins see this. The header doubles as
              a collapse toggle so the panel can shrink to just the base set
              when the operator wants more room for everyday navigation. */}
          {adminNavItems.length > 0 && (
            <>
              {(!collapsed || isMobile) ? (
                <button
                  type="button"
                  className="mn-nav-section is-clickable"
                  onClick={() => setAdminExpanded((v) => !v)}
                  aria-expanded={adminExpanded}
                >
                  <span>Admin</span>
                  <span className="mn-nav-section-rule" />
                  <span className="mn-nav-section-chev">
                    {adminExpanded ? <ChevUp /> : <ChevDown />}
                  </span>
                </button>
              ) : (
                <div className="mn-nav-section" aria-hidden>Admin</div>
              )}
              <Collapse in={adminExpanded || (collapsed && !isMobile)} timeout="auto" unmountOnExit>
                {adminNavItems.map((item) => (
                  <React.Fragment key={item.id}>{renderNavItem(item)}</React.Fragment>
                ))}
              </Collapse>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="mn-nav-foot">
          {/* Theme toggle */}
          {(() => {
            const themeBtn = (
              <button
                type="button"
                className="mn-nav-theme"
                onClick={toggleTheme}
                aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                <span className="mn-nav-theme-icon">
                  {isDarkMode ? <LightModeIcon /> : <DarkModeIcon />}
                </span>
                <span className="mn-nav-theme-text">{isDarkMode ? 'Light Mode' : 'Dark Mode'}</span>
                <span className={`mn-nav-theme-switch${isDarkMode ? ' is-on' : ''}`} aria-hidden />
              </button>
            );
            return isCollapsed ? (
              <Tooltip title={isDarkMode ? 'Light Mode' : 'Dark Mode'} placement="right">
                {themeBtn}
              </Tooltip>
            ) : themeBtn;
          })()}

          {/* Settings — re-uses nav-item class */}
          {renderNavItem({ id: 'settings', label: 'Settings', icon: <SettingsIcon />, path: '/org/settings' })}

          {/* User profile */}
          {(() => {
            const userBtn = (
              <button type="button" className="mn-nav-user" onClick={handleLogout} aria-label="Sign out">
                <span className="mn-nav-user-avatar">{userInitial}</span>
                <span className="mn-nav-user-meta">
                  <span className="mn-nav-user-name">{userName}</span>
                  <span className="mn-nav-user-role">{displayRole(user?.Role || 'Org Admin')}</span>
                </span>
                <span className="mn-nav-user-logout">
                  <LogoutIcon style={{ fontSize: 16 }} />
                </span>
              </button>
            );
            return isCollapsed ? (
              <Tooltip title={`Sign out — ${userName}`} placement="right">
                {userBtn}
              </Tooltip>
            ) : userBtn;
          })()}
        </div>
      </nav>
    );
  };

  return (
    <SessionTimeoutProvider>
      <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--surface-onyx)' }}>
        {/* Mobile top bar */}
        {isMobile && (
          <div className={`mn-shell-mobile-bar${isDarkMode ? ' dark' : ''}`}>
            <button
              type="button"
              className="mn-shell-mobile-burger"
              onClick={() => setMobileOpen(true)}
              aria-label="Open navigation"
            >
              <BurgerSvg />
            </button>
            <div className="mn-shell-mobile-brand">
              <LogoIcon size={22} color="currentColor" className="mn-shell-mobile-brand-logo" />
              <span>MedNecessity</span>
            </div>
          </div>
        )}

        {/* Desktop sidebar (fixed) */}
        {!isMobile && (
          <div style={{ position: 'fixed', top: 0, left: 0, height: '100vh', zIndex: 1100 }}>
            <NavContent />
          </div>
        )}

        {/* Mobile drawer */}
        <Drawer
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          sx={{
            '& .MuiDrawer-paper': {
              bgcolor: 'transparent',
              border: 'none',
              borderRadius: 0,
              backgroundImage: 'none',
            },
          }}
        >
          <NavContent />
        </Drawer>

        {/* Main content */}
        <main
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minHeight: '100vh',
            overflow: 'auto',
            marginLeft: isMobile ? 0 : `${navWidth}px`,
            paddingTop: isMobile ? '56px' : 0,
            transition: 'margin-left 0.22s cubic-bezier(0.16, 1, 0.3, 1)',
          }}
        >
          <Outlet />
        </main>
      </div>
    </SessionTimeoutProvider>
  );
};

export default OrgLayout;
